import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@copro/api";
import { useSpace } from "../../../hooks/useSpace";
import { SubjectPendingItemDatabase } from "../../../types";
import { keys } from "./keys";

type UpdateParams = {
  id: number;
  title?: string;
  provider_name?: string | null;
  provider_id?: string | null;
  state?: 'pending' | 'completed';
  completed_at?: string | null;
};

export function useUpdatePendingItem(subjectId: string) {
  const queryClient = useQueryClient();
  const { currentSpace } = useSpace();

  return useMutation({
    mutationFn: async ({ id, ...updates }: UpdateParams) => {
      const { data, error } = await apiClient
        .from('subject_pending_items')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onMutate: async (newValues) => {
      await queryClient.cancelQueries({ queryKey: keys.getPendingItems(subjectId) });
      const previousItems = queryClient.getQueryData<SubjectPendingItemDatabase[]>(keys.getPendingItems(subjectId));

      queryClient.setQueryData(keys.getPendingItems(subjectId), (old: SubjectPendingItemDatabase[] | undefined) =>
        old?.map((i) => (i.id === newValues.id ? { ...i, ...newValues } : i))
      );

      return { previousItems };
    },
    onError: (_err, _newValues, context) => {
      queryClient.setQueryData(keys.getPendingItems(subjectId), context?.previousItems);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: keys.getPendingItems(subjectId) });
      if (currentSpace?.id) {
        queryClient.invalidateQueries({ queryKey: keys.pendingSubTopics(currentSpace.id) });
        queryClient.invalidateQueries({ queryKey: keys.openPendingItems(currentSpace.id) });
      }
    },
  });
}
